import type { StoreListDto } from "@/types/store.dto";
import type { ProductListDto } from "@/types/product.dto";
import type { DeliveryPartnerListDto } from "@/types/delivery.dto";
import type { PaymentListDto } from "@/types/payment.dto";
import { PaymentMethod, PaymentStatus } from "@/types/enums";
import type { AdminStore, AdminProduct, DeliveryPartner } from "@/types/admin.ui";
import { DeliveryPartnerStatus, ProductStatus, StoreStatus } from "@/types/enums";
import { resolveUploadUrl } from "@/services/filesService";

const DEFAULT_PRODUCT_IMAGE = "";

export function mapStoreStatusToUi(status: StoreStatus): AdminStore["status"] {
  switch (status) {
    case StoreStatus.Active:
      return "approved";
    case StoreStatus.Suspended:
      return "suspended";
    case StoreStatus.Rejected:
      return "rejected";
    case StoreStatus.Pending:
    default:
      return "pending";
  }
}

export function mapProductStatusToUi(status: ProductStatus): AdminProduct["status"] {
  switch (status) {
    case ProductStatus.Approved:
      return "approved";
    case ProductStatus.Rejected:
      return "rejected";
    case ProductStatus.NeedsRevision:
      return "needs_revision";
    case ProductStatus.Pending:
    default:
      return "pending";
  }
}

export function mapDeliveryStatusToUi(
  status: DeliveryPartnerStatus,
): DeliveryPartner["status"] {
  switch (status) {
    case DeliveryPartnerStatus.Active:
      return "active";
    case DeliveryPartnerStatus.Suspended:
      return "inactive";
    case DeliveryPartnerStatus.Rejected:
      return "inactive";
    case DeliveryPartnerStatus.Pending:
    default:
      return "pending";
  }
}

export function mapStoreListDtoToAdminStore(dto: StoreListDto): AdminStore {
  return {
    id: String(dto.id),
    name: dto.storeName,
    ownerName: dto.ownerName ?? "—",
    email: dto.email ?? "—",
    phone: dto.phone ?? "—",
    address: dto.city,
    status: mapStoreStatusToUi(dto.status),
    joinedDate: dto.createdAt ? new Date(dto.createdAt).toLocaleDateString() : "—",
    totalProducts: 0,
    totalOrders: dto.totalOrders,
    totalRevenue: 0,
    commissionRate: 0,
    rating: dto.rating,
    logoUrl: dto.logoUrl ? resolveUploadUrl(dto.logoUrl) : undefined,
    ownerUserId: dto.ownerUserId,
  };
}

export function mapProductListDtoToAdminProduct(dto: ProductListDto): AdminProduct {
  return {
    id: String(dto.id),
    name: dto.name,
    storeName: dto.storeName,
    storeId: String(dto.storeId),
    category: dto.categoryName,
    price: dto.sellingPrice,
    status: mapProductStatusToUi(dto.status),
    submittedDate: dto.createdAt ? new Date(dto.createdAt).toLocaleDateString() : "—",
    image: resolveUploadUrl(dto.primaryImage ?? DEFAULT_PRODUCT_IMAGE),
    stock: dto.stockQuantity,
    isPublished: dto.isActive,
  };
}

export function mapDeliveryPartnerListDtoToAdmin(dto: DeliveryPartnerListDto): DeliveryPartner {
  return {
    id: String(dto.id),
    name: dto.fullName,
    phone: dto.phone,
    email: "—",
    vehicleType: dto.vehicleType,
    status: mapDeliveryStatusToUi(dto.status),
    totalDeliveries: dto.totalDeliveries,
    totalEarnings: 0,
    rating: dto.rating,
    joinedDate: "—",
  };
}

export function mapStoreListDtosToAdminStores(items: StoreListDto[]): AdminStore[] {
  return items.map(mapStoreListDtoToAdminStore);
}

export function mapProductListDtosToAdminProducts(items: ProductListDto[]): AdminProduct[] {
  return items.map(mapProductListDtoToAdminProduct);
}

export function mapDeliveryPartnerListDtosToAdmin(items: DeliveryPartnerListDto[]): DeliveryPartner[] {
  return items.map(mapDeliveryPartnerListDtoToAdmin);
}

export interface AdminPaymentRow {
  id: string;
  orderNumber: string;
  paymentId: string;
  amount: number;
  status: string;
  method: string;
  date: string;
}

export function mapPaymentListDtoToAdminRow(dto: PaymentListDto): AdminPaymentRow {
  return {
    id: String(dto.id),
    orderNumber: dto.orderNumber,
    paymentId: dto.paymentId,
    amount: dto.amount,
    status: mapPaymentStatusLabel(dto.status),
    method: mapPaymentMethodLabel(dto.method),
    date: dto.paidAt ?? dto.createdAt,
  };
}

function mapPaymentStatusLabel(status: PaymentStatus): string {
  switch (status) {
    case PaymentStatus.Completed:
      return "completed";
    case PaymentStatus.Failed:
      return "failed";
    case PaymentStatus.Refunded:
      return "refunded";
    default:
      return "pending";
  }
}

function mapPaymentMethodLabel(method: PaymentMethod): string {
  switch (method) {
    case PaymentMethod.COD:
      return "cod";
    case PaymentMethod.Wallet:
      return "wallet";
    default:
      return "razorpay";
  }
}

export function mapPaymentListDtosToAdminRows(items: PaymentListDto[]): AdminPaymentRow[] {
  return items.map(mapPaymentListDtoToAdminRow);
}
